import { EventEmitter, Injectable } from '@angular/core';
import { PuzzleFrameworkEvents } from './puzzle-framework-events.enum';
import { BehaviorSubject, Observable, Subscription, firstValueFrom } from 'rxjs';
import { PuzzleTypeIdEnum } from './puzzle-type-id.enum';
import { PuzzleFrameworkConfigModel } from '../store/puzzle-framework-config.model';
import { PuzzleGameModel } from '../store/puzzle-game.model';
import { Store } from '@ngxs/store';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AlertController, ModalController, ToastController } from '@ionic/angular/standalone';
import { PuzzleGameState } from '../store/puzzle-game.state';
import { PuzzleTimeRecordState } from '../store/puzzle-time-record.state';
import { map } from 'rxjs/operators';
import { PuzzleGameTypesEnum } from './puzzle-game-types.enum';
import {
  InitPuzzleGame,
  IncMoveCounter,
  ResetPause,
  PauseGame,
  ResumeGame,
  InitDefaultValuesFromConfig,
  ResetLetterSkipSettings,
  SetKeepKeyboardOpen,
  SetNativeKeyboardEnabled,
  SetGameDifficulty,
  SetLanguage,
  SetPuzzleGame,
} from '../store/puzzle-game.actions';
import { ResetPuzzleID } from '../store/puzzle-id.actions';
import { UpdateFrameworkConfig } from '../store/puzzle-framework-config.actions';
import { LoadUpdateNotifications, MarkAsRead } from '../store/puzzle-update-notifications.actions';
import { Md5 } from 'ts-md5';
import { PuzzleFileInfoModel } from '../store/puzzle-file-info.model';
import { DatePipe } from '@angular/common';
import X2JS from 'x2js';
import * as YAML from 'js-yaml';
import { HelperFunctionsService } from '../shared-module/helpers/helper-functions.service';
import { addIcons } from 'ionicons';
import { closeSharp } from 'ionicons/icons';
import { generateHashiPuzzle } from '../hashi-module/generator/generate-hashi-puzzle';

import { environment } from 'projects/ionic/src/environments/environment';

import { buildEnv } from 'build-environment';
import { DarkModes, WordJumpOnRestrictions, WordJumpToRestrictions } from '../store/puzzle-game-state.model';
import { DomSanitizer } from '@angular/platform-browser';
import { SwedishGameState } from '../crossword-module/store/swedish-game.state';
import { SwedishPuzzleModel } from '../crossword-module/store/swedish-game.model';
import { PersistUserInput } from '../crossword-module/store/swedish-game.actions';
import { PuzzleTimeRecordModel } from '../store/puzzle-time-record.model';
import { InitTimeRecord } from '../store/puzzle-time-record.actions';

const __PROJECT_NAME__: string = buildEnv.projectName;
const __BUILD_VERSION__: string = buildEnv.buildVersion;

// Declare a property on window. Needed for PuzzleFrameworkService.getTcfData()
// Info from https://stackoverflow.com/a/56458070/4396362
declare global {
  interface Window {
    __tcfapi: Function;
  }
}

interface PMParentMessage {
  type: string;
  puzzleID?: string;
  requestId?: string;
  puzzleGameState?: PuzzleGameModel;
  puzzleTimeState?: PuzzleTimeRecordModel;
  swedishGameState?: SwedishPuzzleModel;
}

@Injectable({
  providedIn: 'root',
})
export class PuzzleFrameworkService {
  /* Event emitter proxy
   * Each individual puzzle will subscribe their button behavior to this Emitter, and
   * Components such as the keyboard or buttons .
   * A puzzle component will emit the event via PuzzleFrameworkService.buttonBarEmitButton(),
   * and receive the event via a subscription to PuzzleFrameworkService.buttonPressed
   * The keyboard inputs however will not be handled via this emitter, but
   * PuzzleKeyboardService.softKeyboardEmit() and PuzzleKeyboardService.onKeyDown.
   */
  buttonPressed: EventEmitter<string> = new EventEmitter<string>();
  puzzleEvents: EventEmitter<PuzzleFrameworkEvents> = new EventEmitter<PuzzleFrameworkEvents>();

  // TODO: refactor: puzzleTypeIdLoaded - this is NOT the config that is broadcast on this subject,
  // but the type of the config loaded.
  private puzzleConfigLoaded: BehaviorSubject<PuzzleTypeIdEnum> = new BehaviorSubject<PuzzleTypeIdEnum>(null);
  readonly puzzleConfigLoaded$: Observable<PuzzleTypeIdEnum> = this.puzzleConfigLoaded.asObservable();

  // Used for subscribing to tcf stuff finished loading.
  private tcfConsentLoaded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly tcfConsentLoaded$: Observable<boolean> = this.tcfConsentLoaded.asObservable();

  initialized = false;
  puzzleFrameworkConfig: PuzzleFrameworkConfigModel;
  configPuzzleName: string;
  selectedPuzzleName: string;
  puzzleConfig: any;

  private browserWarning: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly browserWarning$: Observable<boolean> = this.browserWarning.asObservable();

  hasKeyboardInput = false;

  // TODO: refactor: todayDate?
  dateParameter: Date;

  private currentDifficulty: string;

  public counter = 0;
  private puzzleID: string;
  public urlDateParameterEnabled = false;
  public urlDifficultyParameterEnabled = false;
  public urlDifficultyParameterInPuzzleParameter = false;

  private gdprConsent: boolean;
  private gdprConsentString: string;
  private puzzleGameSubscription: Subscription;
  private puzzleGame: PuzzleGameModel;
  private puzzleTimeSubscription: Subscription;
  // @ts-ignore
  private puzzleTime: TimeRecordModel;
  private currentLanguage: string;
  private removeSideMargins: boolean;
  private forceDarkMode: DarkModes;
  private skipConsent: boolean;
  private pauseDialogOpen: boolean;
  private pausedState: number;
  allowUserToggleDefinitionBar: boolean;
  puzzleFileLoadErrors: string[] = [];

  // set true to enable Rumpelstilzchen without reload
  private softCheatCodeEnabled = false;

  // current non-dismissed toast
  private toast: any;

  // ***********************
  // postMessage integration
  // ***********************
  private pmEnabled = false;
  private pmTrustedOrigin: string = null;
  private pmPendingRequestId: string | null = null;
  private pmAwaitTimer: any = null;
  private pmInitDoneForPuzzleId: string | null = null;
  private _notifyAwaiters(pid: string) {
    const list = this.pmAwaitResolvers[pid];
    if (list && list.length) {
      list.forEach((fn) => {
        try {
          fn();
        } catch {}
      });
      this.pmAwaitResolvers[pid] = [];
    }
  }
  private pmAwaitResolvers: { [pid: string]: Array<() => void> } = {};

  private _gotGameDataFromParent = async (data: PMParentMessage) => {
    // console.log('[PuzzleFrameworkService] got game data from parent', data);
    const rid = data.requestId;
    const pid = data.puzzleID || this.puzzleID;
    if (!pid) {
      console.log('[PuzzleFrameworkService] ignoring game data from parent, no game id');
      return;
    }
    // Correlate only to pending request and ignore late responses (but allow magic request id)
    if (!this.pmPendingRequestId && this.puzzleFrameworkConfig.magicRequestId !== rid) {
      // no request pending anymore -> late response, ignore
      console.log('[PuzzleFrameworkService] ignoring game data from parent, no request pending');
      return;
    }
    if (rid !== this.pmPendingRequestId && this.puzzleFrameworkConfig.magicRequestId !== rid) {
      console.log('[PuzzleFrameworkService] ignoring game data from parent, no matching request');
      return;
    }

    // Clear the timeout and the pending request data
    window.clearTimeout(this.pmAwaitTimer);
    this.pmPendingRequestId = null;
    this.pmInitDoneForPuzzleId = pid;

    if (data.puzzleGameState == null || data.swedishGameState == null || data.puzzleTimeState == null) {
      console.log('[PuzzleFrameworkService] ignoring game data from parent, incomplete data');
      return;
    }

    // Compare the game progress
    const puzzleGameState = (await firstValueFrom(
      this.store.select(PuzzleGameState.getByID(data.puzzleID))
    )) as PuzzleGameModel;
    if (data.puzzleGameState.progress < puzzleGameState?.progress) {
      // Don't overwrite a more advanced game state with a less advanced one
      console.log('[PuzzleFrameworkService] ignoring game data from parent, less advanced game state');
      return;
    }

    if (data.puzzleGameState) {
      await firstValueFrom(this.store.dispatch(new SetPuzzleGame(data.puzzleGameState)));
    }
    // add the time record data
    if (data.puzzleTimeState) {
      await firstValueFrom(
        this.store.dispatch(
          new InitTimeRecord({
            puzzleID: data.puzzleID,
            timeRecord: data.puzzleTimeState.timeRecord,
          })
        )
      );
    }
    // add the swedishGame data
    if (data.swedishGameState) {
      await firstValueFrom(
        this.store.dispatch(
          new PersistUserInput({
            puzzleID: data.puzzleID,
            userInput: data.swedishGameState.userInput,
          })
        )
      );
    }

    // const payload = data.data;
    // let gameObj: any = null;
    // if (payload && payload.puzzleGames && payload.puzzleGames[pid]) {
    //   gameObj = payload.puzzleGames[pid];
    // } else if (payload && payload.puzzleID === pid) {
    //   gameObj = payload;
    // }
    // if (!gameObj) {
    //   return;
    // }
    // try {
    //   await firstValueFrom(this.store.dispatch(new InitPuzzleGame(gameObj)));
    // } catch (e) {
    //   // noop
    // }
  };

  private _sendGameDataToParent = async (requestId?: string, pid?: string) => {
    if (!this.pmEnabled || !this.pmTrustedOrigin) {
      return;
    }
    const puzzleID = pid || this.puzzleID;
    if (!puzzleID) {
      return;
    }

    // Get the userInput
    // TODO adapt for puzzletypes
    const swedishGameState = (await firstValueFrom(
      this.store.select(SwedishGameState.getByID(puzzleID))
    )) as SwedishPuzzleModel;

    // Get the game state
    const puzzleGameState = (await firstValueFrom(
      this.store.select(PuzzleGameState.getByID(puzzleID))
    )) as PuzzleGameModel;

    // Get the time state
    // const puzzleTimeState = await firstValueFrom(this.store.select(PuzzleTimeRecordState.getByID(puzzleID))) as PuzzleTimeRecordModel;

    const puzzleTimeState = (await firstValueFrom(
      this.store.select(PuzzleTimeRecordState.getTimeRecordByID).pipe(map((filterFn) => filterFn(puzzleID)))
    )) as PuzzleTimeRecordModel;

    const msg: PMParentMessage = { type: 'gameData', puzzleID, puzzleGameState, puzzleTimeState, swedishGameState };

    if (requestId) {
      msg.requestId = requestId;
    }
    try {
      window.parent.postMessage(msg, this.pmTrustedOrigin);
    } catch (e) {
      // noop
    }
  };

  private _requestParentGameData = (puzzleID: string) => {
    // Don't do it if not enabled
    if (!this.pmEnabled || !this.pmTrustedOrigin) {
      return;
    }
    // Don't do it if a request is pending already
    if (this.pmPendingRequestId) {
      return;
    }
    // Don't do it if we don't have a puzzleID
    if (!puzzleID) {
      return;
    }
    const rid =
      typeof crypto !== 'undefined' && (crypto as any).randomUUID
        ? (crypto as any).randomUUID()
        : `${Date.now()}-${Math.random()}`;
    this.pmPendingRequestId = rid;

    // We automatically stop waiting after some time
    this.pmAwaitTimer = window.setTimeout(() => {
      // Timeout: ignore any late responses
      if (this.pmPendingRequestId === rid) {
        this.pmPendingRequestId = null;
        this.pmInitDoneForPuzzleId = puzzleID;
        this._notifyAwaiters(puzzleID);
      }
    }, this.puzzleFrameworkConfig.pmTimeout);

    const msg: any = { type: 'getGameData', puzzleID, requestId: rid };
    try {
      window.parent.postMessage(msg, this.pmTrustedOrigin);
    } catch (e) {
      // noop
    }
  };

  private _onWindowMessage = async (event: MessageEvent) => {
    // console.log('[PuzzleFrameworkService: got window message', event);
    if (!this.pmEnabled || !this.pmTrustedOrigin) {
      // console.log('[PuzzleFrameworkService] ignoring message, pm not enabled');
      return;
    }
    if (!event || event.origin !== this.pmTrustedOrigin || event.source !== window.parent) {
      // console.log('[PuzzleFrameworkService] ignoring message, origin mismatch');
      return;
    }

    const data = event.data as PMParentMessage;
    if (!data || typeof data !== 'object' || !data.type) {
      // console.log('[PuzzleFrameworkService] ignoring message, invalid data');
      return;
    }
    // console.log('[PuzzleFrameworkService: got valid message data', data);

    // Parent asks for game data
    if (data.type === 'getGameData') {
      await this._sendGameDataToParent(data.requestId, data.puzzleID || this.puzzleID);
      return;
    }

    // Parent responds with game data
    if (data.type === 'setGameData') {
      await this._gotGameDataFromParent(data);
    }
  };
  // ***********************
  // postMessage integration
  // ***********************

  constructor(
    private store: Store,
    private http: HttpClient,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private ionicAlert: AlertController,
    private translateService: TranslateService,
    private ionicModal: ModalController,
    private toastController: ToastController,
    private helperFunctions: HelperFunctionsService,
    private sanitizer: DomSanitizer
  ) {
    this.store
      .select((state) => state.puzzleGameState.difficulty)
      .subscribe((difficulty) => {
        this.currentDifficulty = difficulty;
      });

    this.store
      .select((state) => state.puzzleGameState.language)
      .subscribe((language) => {
        this.currentLanguage = language;
      });

    this.store
      .select((state) => state.puzzleGameState.paused)
      .subscribe((paused) => {
        this.pausedState = paused;
      });

    this.store
      .select((state) => state.puzzleID)
      .subscribe((puzzleID) => {
        this.puzzleID = puzzleID;

        // Now request from the parent for postMessage integration
        this._requestParentGameData(this.puzzleID);

        if (this.puzzleID) {
          if (this.puzzleGameSubscription) {
            this.puzzleGameSubscription.unsubscribe();
          }

          this.puzzleGameSubscription = this.store
            .select(PuzzleGameState.getByID(puzzleID))
            .subscribe((puzzleGame: PuzzleGameModel) => {
              if (puzzleGame === null) {
                return;
              }
              this.puzzleGame = puzzleGame;
              // TODO, not sure if it makes sense to set this here. But this enables the reload for now.
              this.dateParameter = this.getCurrentDate();
            });

          if (this.puzzleTimeSubscription) {
            this.puzzleTimeSubscription.unsubscribe();
          }

          this.puzzleTimeSubscription = this.store
            .select(PuzzleTimeRecordState.getTimeRecordByID)
            .pipe(map((filterFn) => filterFn(puzzleID)))
            .subscribe((puzzleTime) => {
              if (!puzzleTime) {
                return;
              }
              this.puzzleTime = puzzleTime.timeRecord;
            });
        }
      });

    this.store
      .select((state) => state.puzzleFrameworkConfig)
      .subscribe((puzzleFrameworkConfig) => {
        if (!puzzleFrameworkConfig) {
          return;
        }
        this.puzzleFrameworkConfig = puzzleFrameworkConfig;

        // ***********************
        // postMessage integration
        // ***********************
        // postMessage enable/disable based on trustedParentURL
        const trustedParentUrl = this.puzzleFrameworkConfig.trustedParentURL;
        let origin: string = null;
        try {
          if (trustedParentUrl) {
            origin = new URL(trustedParentUrl).origin;
          }
        } catch (e) {
          origin = null;
        }
        const shouldEnable = !!origin;
        if (shouldEnable && !this.pmEnabled) {
          // Should enable and not yet enabled
          this.pmTrustedOrigin = origin;
          this.pmEnabled = true;
          window.addEventListener('message', this._onWindowMessage);
        } else if (!shouldEnable && this.pmEnabled) {
          // Should not enable and is enabled
          this.pmEnabled = false;
          this.pmTrustedOrigin = null;
          window.removeEventListener('message', this._onWindowMessage);
        } else if (shouldEnable && this.pmEnabled) {
          // Should enable and is enabled already
          // update origin if it changed
          this.pmTrustedOrigin = origin;
        }
        // ***********************
        // postMessage integration
        // ***********************
      });
  }

  // TODO: why is this here? (the dialog opening part i mean)
  // Maybe split those kind of things into a "UIService", that could be a proxy for i.e.:
  // IonicUIService
  // StandaloneWebUIService
  // EmbeddedWebUIService
  public async openPauseDialog(stopTimer = true) {
    if (this.pauseDialogOpen || this.pausedState > 0) {
      return;
    }
    this.pauseDialogOpen = true;

    if (stopTimer) {
      this.pauseCurrentGame();
    }

    const resolved = await Promise.all([
      firstValueFrom(this.translateService.get('PUZZLE.pause_dialog.title')),
      firstValueFrom(this.translateService.get('PUZZLE.pause_dialog.text')),
      firstValueFrom(this.translateService.get('PUZZLE.pause_dialog.button')),
    ]);

    const alert = await this.ionicAlert.create({
      header: resolved[0],
      message: resolved[1],
      cssClass: 'puzzle-alert',
      buttons: [
        {
          text: resolved[2],
          role: 'confirm',
          cssClass: 'primary',
        },
      ],
    });

    await alert.present();

    if (stopTimer) {
      await alert.onDidDismiss();
      this.resumeCurrentGame();
    }

    this.pauseDialogOpen = false;
  }

  public puzzleHasSolutionWord(): boolean {
    return this.puzzleGame ? !!this.puzzleGame.solutionWord : false;
  }

  public playSound(source: string) {
    const audio = new Audio();
    audio.src = source;
    audio.load();
    audio.play();
  }

  public checkGdprParametersAndReload() {
    const parameters = this.getHashParameters();

    if (!this.puzzleFrameworkConfig || !this.configPuzzleName) {
      this.gdprConsentString = parameters.hasOwnProperty('GDPR_CONSENT_STRING')
        ? parameters.GDPR_CONSENT_STRING
        : undefined;
      this.gdprConsent = parameters.hasOwnProperty('GDPR') ? parameters.GDPR : undefined;
      return;
    }

    if (
      this.gdprConsentString !==
        (parameters.hasOwnProperty('GDPR_CONSENT_STRING') ? parameters.GDPR_CONSENT_STRING : undefined) ||
      this.gdprConsent !== (parameters.hasOwnProperty('GDPR') ? parameters.GDPR : undefined)
    ) {
      window.location.reload();
    }
  }

  // TODO: refactor: conditionalWindowReload()
  // mir ist nicht ganz klar, wieso das mit window.reload gemacht wird :)
  public checkHashParametersAndReload() {
    // do nothing if we're not initialized yet
    if (!this.puzzleFrameworkConfig || !this.configPuzzleName) {
      return;
    }

    const parameters = this.getHashParameters();
    const puzzleName = parameters.hasOwnProperty('puzzle')
      ? parameters.puzzle
      : this.puzzleFrameworkConfig.defaultPuzzle;

    // TODO: refactor: this.reloadIfUrlPuzzleNameChanged
    if (this.puzzleFrameworkConfig.puzzleFiles.hasOwnProperty(puzzleName) && puzzleName !== this.configPuzzleName) {
      window.location.reload();
    }

    // TODO: refactor: this.reloadIfDateChanged()
    if (this.dateParameter && this.getCurrentDate().toDateString() !== this.dateParameter.toDateString()) {
      window.location.reload();
    }

    // TODO: refactor: this.reloadIfUrlDifficultyChanged()
    if (this.puzzleFrameworkConfig.urlDifficultyParameterEnabled) {
      const difficulty = this.getDifficultyFromUrl() || this.puzzleFrameworkConfig.difficultyDefault;
      if (difficulty !== this.currentDifficulty) {
        window.location.reload();
      }
    }

    // TODO: refactor: this.reloadIfUrlLanguageChanged
    if (this.puzzleFrameworkConfig.urlLanguageParameterEnabled) {
      const language = this.getLanguageParameterFromUrl() || this.puzzleFrameworkConfig.defaultLanguage;
      if (language !== this.currentLanguage) {
        window.location.reload();
      }
    }

    // TODO: refactor: this.reloadIfUrlLanguageChanged
    if (this.puzzleFrameworkConfig.removeSideMargins !== undefined) {
      const removeSideMarginsParameterFromUrl = this.getRemoveSideMarginsParameterFromUrl();
      if (removeSideMarginsParameterFromUrl !== this.removeSideMargins) {
        window.location.reload();
      }
    }

    // TODO: refactor
    if (this.puzzleFrameworkConfig.forceDarkMode !== undefined) {
      const forceDarkModeParameterFromUrl = this.getForceDarkModeParameterFromUrl();
      if (forceDarkModeParameterFromUrl !== this.forceDarkMode) {
        window.location.reload();
      }
    }

    // TODO: refactor: this.reloadIfUrlLanguageChanged
    if (this.puzzleFrameworkConfig.consentSkippable) {
      const skipConsentParameterFromUrl = this.getSkipConsentParameterFromUrl();
      if (skipConsentParameterFromUrl !== this.skipConsent) {
        window.location.reload();
      }
    }
  }

  public wordJumpOnDefaultBasedOnDevice(): boolean {
    return this.helperFunctions.isMobileDevice()
      ? this.puzzleFrameworkConfig.wordJumpOnDefaultMobile
      : this.puzzleFrameworkConfig.wordJumpOnDefaultDesktop;
  }

  public autoZoomDefaultBasedOnDevice(): boolean {
    return this.helperFunctions.isMobileDevice()
      ? this.puzzleFrameworkConfig.autoZoomEnabledDefaultMobile
      : this.puzzleFrameworkConfig.autoZoomEnabledDefaultDesktop;
  }

  public definitionBarEnabledDefaultBasedOnDevice(): boolean {
    return this.helperFunctions.isMobileDevice()
      ? this.puzzleFrameworkConfig.definitionBarEnabledDefaultMobile
      : this.puzzleFrameworkConfig.definitionBarEnabledDefaultDesktop;
  }

  public async initPuzzleGameState(
    puzzleID: string,
    gameType: PuzzleGameTypesEnum,
    jokerCount = 0,
    solutionWord?: string,
    solutionOriginal?: string
  ) {
    const data = {
      gameType,
      puzzleID,
      jokerCount,
      sayingFound: false,
      progress: 0,
    } as PuzzleGameModel;

    if (solutionWord) {
      data.solutionWord = solutionWord;
      data.solutionFound = false;
    }

    if (solutionOriginal) {
      data.solutionOriginal = solutionOriginal;
    }

    return this.store.dispatch(new InitPuzzleGame(data));
  }

  public incrementMoveCounter(puzzleID) {
    this.store.dispatch(
      new IncMoveCounter({
        puzzleID,
      })
    );
  }

  // TODO: understand why this is "if this.puzzleID"
  public resetPauseCounter(): boolean {
    if (this.puzzleID) {
      this.store.dispatch(new ResetPause({ puzzleID: this.puzzleID }));
      return true;
    }
    return false;
  }

  public pauseCurrentGame(): boolean {
    if (this.puzzleID) {
      this.store.dispatch(new PauseGame({ puzzleID: this.puzzleID }));
      return true;
    }
    return false;
  }

  public resumeCurrentGame(): boolean {
    if (this.puzzleID) {
      this.store.dispatch(new ResumeGame({ puzzleID: this.puzzleID }));
      return true;
    }
    return false;
  }

  public sendSolutionWordData(solutionWord?: string) {
    // If solutionWord parameter is not set, take parameter from puzzleGame
    if (solutionWord === undefined || solutionWord === null) {
      solutionWord = this.puzzleGame.solutionWord;
    }

    const data: any = {};
    // Gather data for sweepstake page
    data.uuid = this.puzzleGame.solutionFound ? this.puzzleGame.solutionUUID : this.puzzleGame.winUUID;
    data.solution_word = solutionWord;
    data.puzzle_id = this.puzzleID;
    data.move_counter = this.puzzleGame.moveCounter;
    data.game_type = this.puzzleGame.gameType;
    data.joker_count = this.puzzleGame.jokerCount;

    // currently solutionOriginal is only taken from redux @TODO: should at some point be taken from function argument aswell
    if (this.puzzleGame.solutionOriginal) {
      data.solution_original = this.puzzleGame.solutionOriginal;
    }

    // Get gameTime from redux store
    if (this.puzzleTime) {
      data.game_time = this.puzzleTime.gameTime;
    }

    // add md5 hash over the gathered data
    data.activation_key = Md5.hashStr(JSON.stringify(data));

    // backend url should support $language tag
    let url = this.puzzleFrameworkConfig.solutionWordConfig.sweepstake.backendUrl;
    if (url.indexOf('$language') !== -1) {
      url = this.replaceLanguageTag(url);
    }

    const method = this.puzzleFrameworkConfig.solutionWordConfig.sweepstake.requestType;

    const requestTarget = this.puzzleFrameworkConfig.solutionWordConfig.sweepstake.targetBlank ? '_blank' : '_self';

    switch (method) {
      case 'GET':
        const parameterString = Object.entries(data)
          .map((keyValue) => keyValue.map(encodeURIComponent).join('='))
          .join('&');
        window.open(url + '?' + parameterString, requestTarget);
        break;
      case 'POST':
        const form = document.createElement('form');
        form.setAttribute('method', 'post');
        form.setAttribute('action', url);
        form.setAttribute('target', requestTarget);

        for (const key in data) {
          if (data.hasOwnProperty(key)) {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = key;
            input.value = data[key];
            form.appendChild(input);
          }
        }
        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
        break;
    }
  }

  public async showTranslatedToast(
    translationString: string,
    duration = 1500,
    button?: { text: string; icon: string; handler: () => void }
  ) {
    // Need to load the close icon
    addIcons({ closeSharp });

    if (this.toast) {
      await this.toast.dismiss();
    }

    const message = await firstValueFrom(this.translate.get(translationString));

    const buttons = [];

    if (button) {
      buttons.push({
        text: await firstValueFrom(this.translate.get(button.text)),
        icon: button.icon,
        side: 'end',
        role: 'button',
        handler: button.handler,
      });
    }

    buttons.push({
      icon: 'close-sharp',
      role: 'cancel',
      handler: () => {},
    });

    this.toast = await this.toastController.create({
      message,
      duration,
      buttons,
    });

    this.toast.onDidDismiss().then(() => {
      this.toast = undefined;
    });

    await this.toast.present();
  }

  public initPuzzleConfig(puzzleFileData: any) {
    this.counter++;

    firstValueFrom(this.store.selectOnce((state) => state.puzzleFrameworkConfig)).then((puzzleFrameworkConfig) => {
      this.puzzleFrameworkConfig = puzzleFrameworkConfig;
      // if (!this.initialized) {
      this.initPuzzle(puzzleFileData);
    });
  }

  public unloadPuzzle() {
    this.store.dispatch(new ResetPuzzleID());
  }

  /**
   * Loads YAML from an HTTP path, and updates (Object.assign()) state.puzzleFrameworkConfig with it.
   *
   * @param puzzleFrameworkConfigYAMLPath Path of the YAML file to load
   */
  private async _updatePuzzleFrameworkConfigStateWithYAML(puzzleFrameworkConfigYAMLPath: string) {
    try {
      const puzzleFrameworkConfigYAML = await firstValueFrom(
        this.http.get(puzzleFrameworkConfigYAMLPath, {
          responseType: 'text',
          params: {
            version: __BUILD_VERSION__,
          },
        })
      );

      const puzzleFrameworkConfig = YAML.load(puzzleFrameworkConfigYAML);

      return firstValueFrom(this.store.dispatch(new UpdateFrameworkConfig({ puzzleFrameworkConfig })));
    } catch (error) {
      console.log(
        `_updatePuzzleFrameworkConfigStateWithYAML: error trying to load path '${puzzleFrameworkConfigYAMLPath}'`
      );
      console.error(error);
      return Promise.reject(error);
    }
  }

  /*
   * Updates state.puzzleFrameworkConfig from two locations:
   *  - assets/base.config.yaml, which will contain all necessary configuration options to not throw
   *    exceptions
   *  - assets/projects/<project_name>/config.yaml, which will contain updated configuration
   *    options specific to a project or customer
   *
   * Attention:
   *  - only the first level keys of the project config will overwrite the first level keys of the
   *    base.config.yaml (Object.assign is used in the update action)
   *  - this method will not re-initialize or reset a bad config. If the base.config.yaml and the
   *    <project>/config.yaml do not include all necessary configuration options, pieces of a previous
   *    config may remain
   */

  private async loadProjectYAMLConfigsToState() {
    // TODO? get the magic strings from environment
    const puzzleFrameworkBaseConfigPath = 'assets/default.config.yaml';
    await this._updatePuzzleFrameworkConfigStateWithYAML(puzzleFrameworkBaseConfigPath);

    const puzzleFrameworkProjectConfigPath = 'assets/projects/' + __PROJECT_NAME__ + '/config.yaml';
    await this._updatePuzzleFrameworkConfigStateWithYAML(puzzleFrameworkProjectConfigPath);
  }

  private getPuzzleConfigFromHashParameters(): PuzzleFrameworkConfigModel {
    const hashParameters = this.getHashParameters();
    const config: PuzzleFrameworkConfigModel = {};

    Object.keys(hashParameters).forEach((key) => {
      if (!this.puzzleFrameworkConfig || !this.puzzleFrameworkConfig.hasOwnProperty(key)) {
        return;
      }
      switch (typeof this.puzzleFrameworkConfig[key]) {
        case 'object':
          // no objects supported yet
          break;
        case 'boolean':
          if (['1', 'true'].indexOf(hashParameters[key]) !== -1) {
            config[key] = true;
          } else if (['0', 'false'].indexOf(hashParameters[key]) !== -1) {
            config[key] = false;
          }
          break;
        case 'string':
          config[key] = hashParameters[key];
          break;
        case 'number':
          // // if value is a float, parse it as float, otherwise as int
          // // detect float by checking if the value contains a dot
          // let num = NaN;
          // if (hashParameters[key].indexOf('.') !== -1) {
          //   num = parseFloat(hashParameters[key]);
          // } else {
          //   num = parseInt(hashParameters[key], 10);
          // }

          // Maybe float is just enough
          const num = parseFloat(hashParameters[key]);

          // If the parsed number is not NaN, set it as config value
          if (!isNaN(num)) {
            config[key] = num;
          }
          break;
      }
    });
    return config;
  }

  // TODO: how to make that compatible with ionic
  public getHashParameters() {
    // Could be replaced with more versatile getUrlVars() function in t-online/index.html:171. That detects #?& as delimiters
    let fragment = window.location.hash;
    const values: any = {};

    if (fragment) {
      fragment = fragment.indexOf('#') === -1 ? '#' : fragment;
      fragment = fragment.split('#')[1];

      fragment.split('&').map((text) => {
        const split = text.split('=');
        values[split[0]] = split[1];
      });
    }
    return values;
  }

  public async initDefaultSettings(reset = false) {
    this.store.dispatch(
      new InitDefaultValuesFromConfig({
        autoZoomEnabled: this.autoZoomDefaultBasedOnDevice(),
        definitionBarEnabled: this.helperFunctions.isMobileDevice()
          ? this.puzzleFrameworkConfig.definitionBarEnabledDefaultMobile
          : this.puzzleFrameworkConfig.definitionBarEnabledDefaultDesktop,
        keepKeyboardOpen: this.puzzleFrameworkConfig.keepKeyboardOpen,
        nativeKeyboardEnabled: this.puzzleFrameworkConfig.nativeKeyboard,
        highlightRightAndWrongEnabled: this.puzzleFrameworkConfig.highlightRightAndWrongEnabledDefault,
        showErrorsImmediately: this.puzzleFrameworkConfig.showErrorsImmediatelyEnabledDefault,
        reset,
        wordJumpOnRestriction: this.wordJumpOnDefaultBasedOnDevice()
          ? WordJumpOnRestrictions.CorrectWord
          : WordJumpOnRestrictions.None,
        wordJumpToRestriction: WordJumpToRestrictions.IncorrectWord,
      })
    );

    if (reset) {
      this.store.dispatch(new ResetLetterSkipSettings());
    }
  }

  public async initIonicPuzzle() {
    // use this method to wrap loading config from i.e. firebase or cache
    const puzzleFrameworkConfig = environment.puzzleFrameworkConfig;
    await firstValueFrom(this.store.dispatch(new UpdateFrameworkConfig({ puzzleFrameworkConfig })));

    // I was hoping this would be a good place to load the override config, but it doesn't work
    // because wo do not hat the puzzleFileInfoName here.
    // Can be tested with different jokerButton.value in the override config
    // console.log("puzzleFrameworkConfig", puzzleFrameworkConfig);
    // const puzzleFileInfo = this.getPuzzleFileInfo(puzzleFileInfoName);
    // if (typeof puzzleFileInfo === 'object' && puzzleFileInfo.hasOwnProperty('configOverride')) {
    //   await firstValueFrom(
    //     this.store.dispatch(
    //       new UpdateFrameworkConfig({
    //         puzzleFrameworkConfig: puzzleFileInfo.configOverride,
    //       })
    //     )
    //   );
    // }

    await this.initDefaultSettings();

    if (!this.puzzleFrameworkConfig.keepKeyboardOpenAllowUserChange) {
      this.store.dispatch(new SetKeepKeyboardOpen({ keepKeyboardOpen: this.puzzleFrameworkConfig.keepKeyboardOpen }));
    }

    if (!this.puzzleFrameworkConfig.nativeKeyboardAllowUserChange) {
      this.store.dispatch(
        new SetNativeKeyboardEnabled({
          nativeKeyboardEnabled: this.puzzleFrameworkConfig.nativeKeyboard,
        })
      );
    }

    await this.loadLanguage();
  }

  public async initWebPuzzle() {
    await this.loadProjectYAMLConfigsToState();

    await this.initDefaultSettings();

    if (!this.puzzleFrameworkConfig.keepKeyboardOpenAllowUserChange) {
      this.store.dispatch(new SetKeepKeyboardOpen({ keepKeyboardOpen: this.puzzleFrameworkConfig.keepKeyboardOpen }));
    }

    if (!this.puzzleFrameworkConfig.nativeKeyboardAllowUserChange) {
      this.store.dispatch(
        new SetNativeKeyboardEnabled({
          nativeKeyboardEnabled: this.puzzleFrameworkConfig.nativeKeyboard,
        })
      );
    }

    const values = this.getHashParameters();

    // Make sure the puzzle hash param is given.
    // Only warn, because we have a default puzzle as well.
    if (values.puzzle === undefined) {
      console.warn(
        `No puzzle config name given. Loading default puzzle "${this.puzzleFrameworkConfig.defaultPuzzle}".`
      );
      values.puzzle = this.puzzleFrameworkConfig.defaultPuzzle;
      // throw new Error(`No puzzle config name given.`);
    }
    // Make sure the config is valid.
    if (values.puzzle !== undefined && !this.puzzleFrameworkConfig.puzzleFiles.hasOwnProperty(values.puzzle)) {
      console.error(`Invalid puzzle config name given.`);
      // throw new Error(`Invalid puzzle config name given.`);
    }

    if (values.hasOwnProperty('puzzle') && this.puzzleFrameworkConfig.puzzleFiles.hasOwnProperty(values.puzzle)) {
      this.selectedPuzzleName = values.puzzle;
      // this.configPuzzleName = values.puzzle;
      await this.initPuzzleFrameworkConfig(values.puzzle);
      this.loadPuzzleFile(values.puzzle)
        .then((puzzleFile) => {
          this.initPuzzle(puzzleFile);
          this.configPuzzleName = values.puzzle;
          return;
        })
        .catch((err: Error) => {
          switch (true) {
            case err instanceof HttpErrorResponse:
              this.puzzleFileLoadErrors.push(
                'Couldn\'t find puzzle file for the selected puzzle "' + values.puzzle + '". Try fallbackFile.'
              );
              break;
            case err.message === 'No valid xml':
              this.puzzleFileLoadErrors.push(
                'Couldn\'t parse puzzle file for the selected puzzle "' + values.puzzle + '". Try fallbackFile.'
              );
              break;
            case err.message === 'Invalid config':
              this.puzzleFileLoadErrors.push(
                'Invalid config for the selected puzzle "' + values.puzzle + '". Try fallbackFile.'
              );
              break;
          }
          console.warn(this.puzzleFileLoadErrors[this.puzzleFileLoadErrors.length - 1]);
          console.warn(err);

          // Try to load fallback file
          this.loadPuzzleFile(values.puzzle, true)
            .then((puzzleFile) => {
              this.initPuzzle(puzzleFile);
              this.configPuzzleName = values.puzzle;
              return;
            })
            .catch((err2: Error) => {
              // fallbackFile
              switch (true) {
                case err2 instanceof HttpErrorResponse:
                  this.puzzleFileLoadErrors.push(
                    'Couldn\'t find puzzle fallback file for the selected puzzle "' +
                      values.puzzle +
                      '". Try default puzzle.'
                  );
                  break;
                case err2.message === 'No valid xml':
                  this.puzzleFileLoadErrors.push(
                    'Couldn\'t parse puzzle fallback file for the selected puzzle "' +
                      values.puzzle +
                      '". Try default puzzle.'
                  );
                  break;
                case err2.message === 'Invalid config':
                  this.puzzleFileLoadErrors.push(
                    'Invalid config for the selected puzzle "' + values.puzzle + '". Try default puzzle.'
                  );
                  break;
              }
              console.warn(this.puzzleFileLoadErrors[this.puzzleFileLoadErrors.length - 1]);
              console.warn(err2);
              this.loadDefaultPuzzle();
            });
        });
    } else {
      this.selectedPuzzleName = this.puzzleFrameworkConfig.defaultPuzzle;
      this.loadDefaultPuzzle();
    }
  }

  private loadDefaultPuzzle() {
    this.loadPuzzleFile(this.puzzleFrameworkConfig.defaultPuzzle)
      .then((puzzleFile) => {
        this.initPuzzle(puzzleFile);
        this.configPuzzleName = this.puzzleFrameworkConfig.defaultPuzzle;
      })
      .catch((e: Error) => {
        console.warn('Could not load default puzzle. Loading fallback puzzle');
        console.log(e);
        switch (true) {
          case e instanceof HttpErrorResponse:
            this.puzzleFileLoadErrors.push(
              'Couldn\'t find puzzle file for the default puzzle "' + this.puzzleFrameworkConfig.defaultPuzzle + '".'
            );
            break;
          case e.message === 'No valid xml':
            this.puzzleFileLoadErrors.push(
              'Couldn\'t parse puzzle file for the default puzzle "' + this.puzzleFrameworkConfig.defaultPuzzle + '".'
            );
            break;
          case e.message === 'Invalid config':
            this.puzzleFileLoadErrors.push(
              'Invalid config for the default puzzle "' + this.puzzleFrameworkConfig.defaultPuzzle + '".'
            );
            break;
        }
        this.loadPuzzleFile(this.puzzleFrameworkConfig.fallbackPuzzle)
          .then((puzzleFile) => {
            this.initPuzzle(puzzleFile);
            this.configPuzzleName = this.puzzleFrameworkConfig.fallbackPuzzle;
          })
          .catch(() => {
            console.warn('Could not load puzzle file. Please contact admin');
            switch (true) {
              case e instanceof HttpErrorResponse:
                this.puzzleFileLoadErrors.push(
                  'Couldn\'t find puzzle file for the fallback puzzle "' +
                    this.puzzleFrameworkConfig.fallbackPuzzle +
                    '".'
                );
                break;
              case e.message === 'No valid xml':
                this.puzzleFileLoadErrors.push(
                  'Couldn\'t parse puzzle file for the fallback puzzle "' +
                    this.puzzleFrameworkConfig.fallbackPuzzle +
                    '".'
                );
                break;
              case e.message === 'Invalid config':
                this.puzzleFileLoadErrors.push(
                  'Invalid config for the fallback puzzle "' + this.puzzleFrameworkConfig.fallbackPuzzle + '".'
                );
                break;
            }
            this.puzzleConfigLoaded.next(PuzzleTypeIdEnum.ERROR);
          });
      });
  }

  /**
   * Returns the ad configuration for the currently selected puzzle
   */
  public getCurrentPuzzleAds() {
    if (this.configPuzzleName) {
      const puzzleInfo = this.puzzleFrameworkConfig.puzzleFiles[this.configPuzzleName];
      return (puzzleInfo as PuzzleFileInfoModel).advertisements;
    }
    return null;
  }

  /**
   * Returns the adTagUrl for the currently selected puzzle. If the puzzle does not have one, but there is a gobal one, the global one is returned.
   */
  public getCurrentAdTagUrl() {
    // Otherwise get the global adTagUrl.
    if (this.puzzleFrameworkConfig.adTagUrl) {
      // Return generic adTag and replace cache buster if any.
      return this.puzzleFrameworkConfig.adTagUrl
        .replace(this.puzzleFrameworkConfig.adCacheBuster, Date.now().toString())
        .replace('%%GDPR%%', this.puzzleFrameworkConfig.gdpr)
        .replace('%%GDPR_CONSENT_STRING%%', this.puzzleFrameworkConfig.gdpr_consent_string)
        .replace('%%REFERRER_URL%%', document.referrer);
    }

    // Or return nothing.
    return null;
  }

  /**
   * Request the TCFv2 data from the parent frame or #params.
   */
  public getTcfConsentData() {
    // console.log("PuzzleFrameworkService.getTcfData: requesting TCFv2 data from");
    if (this.puzzleFrameworkConfig.tcfV2Enabled) {
      // Get the consent data from the parent frame CMP via postMessage

      // We need to get the consent info from the tcf 2.0 framework
      // This is based on the code Stefan Steurenthaler from Ströer sent me.
      // More info:
      // https://github.com/InteractiveAdvertisingBureau/GDPR-Transparency-and-Consent-Framework/blob/master/TCFv2/
      // IAB%20Tech%20Lab%20-%20CMP%20API%20v2.md#how-can-vendors-that-use-iframes-call-the-cmp-api-from-an-iframe

      // console.log("  -> parent frame");

      const TCF_LOCATOR_NAME = '__tcfapiLocator';
      const queue = {};
      let cmpFrame: Window;
      let eventListenersAdded = false;
      const that = this;

      // Funktion zum Finden des CMP-Frames mithilfe des TCF-Locators
      function findCMPFrame() {
        var f: Window = window;
        while (f) {
          try {
            if (f.frames[TCF_LOCATOR_NAME]) {
              return f;
            }
          } catch (e) {}
          if (f === window.top) {
            break;
          }
          f = f.parent;
        }
        return null;
      }

      // Funktion zum Senden der Nachricht an die CMP
      function callCMP(cmd, version, callback, parameter) {
        if (!cmpFrame) {
          cmpFrame = findCMPFrame();
        }

        if (!cmpFrame) {
          console.warn('CMP-Frame konnte nicht gefunden werden.');
          if (typeof callback === 'function') {
            callback(null, false);
          }
          return;
        }

        var callId = Math.random().toString(36).substring(2) + Date.now();

        // Nachricht erstellen
        const msg = {
          __tcfapiCall: {
            command: cmd,
            version: version,
            callId: callId,
            parameter: parameter,
          },
        };

        // Callback in die Warteschlange stellen
        queue[callId] = callback;

        // Listener für die Antwort hinzufügen (nur einmal)
        if (!eventListenersAdded) {
          window.addEventListener('message', receiveMessage, false);
          eventListenersAdded = true;
        }

        // Nachricht an das CMP-Frame senden
        cmpFrame.postMessage(msg, '*');
      }

      // Funktion zum Empfangen der Antwort vom CMP
      function receiveMessage(event) {
        var data = event.data;

        if (typeof data === 'string') {
          try {
            data = JSON.parse(data);
          } catch (e) {
            return;
          }
        }

        if (data && data.__tcfapiReturn) {
          var returnId = data.__tcfapiReturn.callId;
          var callback = queue[returnId];
          if (typeof callback === 'function') {
            callback(data.__tcfapiReturn.returnValue, data.__tcfapiReturn.success);
            delete queue[returnId];
          }
        }
      }

      // __tcfapi-Funktion definieren, falls nicht vorhanden
      if (typeof window.__tcfapi !== 'function') {
        window.__tcfapi = callCMP;
      }

      // Hinzufügen eines Event Listeners für TC Daten
      window.__tcfapi('addEventListener', 2, function (tcData, success) {
        if (success && tcData) {
          // console.log('TC Data:', tcData);
          // Hier können Sie den TC String weiterverarbeiten
          // Beispiel: var tcString = tcData.tcString;

          // Set the values
          that.setGdprValues(
            tcData.hasOwnProperty('tcString') ? '1' : '0',
            tcData.hasOwnProperty('tcString') ? tcData.tcString : ''
          );
          // Notify, that we loaded
          that.tcfConsentLoaded.next(true);
        } else {
          console.error('Konnte TC Data nicht abrufen.');
          // Set the values to defaults
          that.setGdprValues('0', '');
        }
      });
    } else if (this.puzzleFrameworkConfig.consentURLParameterEnabled) {
      // Get the consent data from the URL parameters
      // console.log("  -> #params");

      const hashParams = this.getHashParameters();

      // Set the values
      this.setGdprValues(
        hashParams.hasOwnProperty('GDPR') ? hashParams.GDPR : '0',
        hashParams.hasOwnProperty('GDPR_CONSENT_STRING') ? hashParams.GDPR_CONSENT_STRING : ''
      );

      // Notify, that we loaded
      this.tcfConsentLoaded.next(true);
    }
  }

  private setGdprValues(gdpr: string, gdprConsentString: string) {
    // To prevent a cannot assign to read only property error, we create a local copy of the config
    // https://stackoverflow.com/a/63684431/4396362
    const localCopy = { ...this.puzzleFrameworkConfig };

    // Set the values
    localCopy.gdpr = gdpr;
    localCopy.gdpr_consent_string = gdprConsentString;

    // And copy it back
    this.puzzleFrameworkConfig = localCopy;
  }

  public getFirstAllowedDate() {
    let minDate = new Date(-8640000000000000);
    if (this.puzzleFrameworkConfig.hasOwnProperty('urlDateParameterMinDate')) {
      minDate = new Date(this.puzzleFrameworkConfig.urlDateParameterMinDate);
    }
    if (this.puzzleFrameworkConfig.hasOwnProperty('urlDateParameterMinDays')) {
      const minDaysCount = this.puzzleFrameworkConfig.urlDateParameterMinDays;
      const minDays = new Date();
      minDays.setDate(minDays.getDate() - minDaysCount);
      if (minDays > minDate) {
        minDate = minDays;
      }
    }
    return minDate;
  }

  public getCurrentDate() {
    let date = new Date();
    const now = new Date(date.valueOf());
    if (this.puzzleFrameworkConfig.urlDateParameterEnabled || this.cheatCodeEnabled()) {
      const hashParameters = this.getHashParameters();

      if (hashParameters.hasOwnProperty('date')) {
        date = new Date(hashParameters.date);
      }

      if (hashParameters.hasOwnProperty('day')) {
        switch (hashParameters.day[0]) {
          case '-':
            const pastDay = parseInt(hashParameters.day.replace('-', ''), 10);
            date.setDate(date.getDate() - pastDay);
            break;
          case '+':
            const futureDay = parseInt(hashParameters.day.replace('+', ''), 10);
            date.setDate(date.getDate() + futureDay);
            break;
        }
      }

      // If we are in the future, but we have not allowed future or cheated, set it to now
      if (
        now.getTime() < date.getTime() &&
        !this.cheatCodeEnabled() &&
        !this.puzzleFrameworkConfig.urlDateParameterEnableFuture
      ) {
        date = now;
      }

      const minDate = this.getFirstAllowedDate();
      if (minDate > date) {
        date = minDate;
      }
    }
    return date;
  }

  private getDifficultyFromUrl(): string | undefined {
    const hashParameters = this.getHashParameters();

    // If wanted and in puzzle parameter, use it.
    if (this.puzzleFrameworkConfig.urlDifficultyParameterInPuzzleParameter && hashParameters.hasOwnProperty('puzzle')) {
      for (const difficulty of this.puzzleFrameworkConfig.difficultyValues) {
        if (hashParameters.puzzle.includes(difficulty)) {
          return difficulty;
        }
      }
    }

    // If in url, use it.
    if (
      hashParameters.hasOwnProperty('difficulty') &&
      this.puzzleFrameworkConfig.difficultyValues.includes(hashParameters.difficulty)
    ) {
      return hashParameters.difficulty;
    }

    return;
  }

  private getSkipConsentParameterFromUrl(): boolean {
    const hashParameters = this.getHashParameters();

    // If in url, use it.
    if (hashParameters.hasOwnProperty('skipConsent')) {
      return hashParameters.skipConsent;
    }
    return;
  }

  private getLanguageParameterFromUrl(): string {
    const hashParameters = this.getHashParameters();

    // If in url, use it.
    if (
      hashParameters.hasOwnProperty('language') &&
      this.puzzleFrameworkConfig.languagePickerValues.map((lang) => lang.name).includes(hashParameters.language)
    ) {
      return hashParameters.language;
    }

    return;
  }

  private getRemoveSideMarginsParameterFromUrl(): boolean {
    const hashParameters = this.getHashParameters();

    // If in url, use it.
    if (hashParameters.hasOwnProperty('removeSideMargins')) {
      return hashParameters.removeSideMargins;
    }
    return;
  }

  private getForceDarkModeParameterFromUrl(): DarkModes {
    const hashParameters = this.getHashParameters();

    // If in url, use it.
    if (hashParameters.hasOwnProperty('forceDarkMode')) {
      return hashParameters.forceDarkMode;
    }
    return;
  }

  /**
   * Updates the difficulty in the current PuzzleGame state, and returns it as string.
   * Gets it from the URL or if not set from the default config.
   * Checks if it is a valid value from the config.
   * TODO: only supports one word difficulty levels atm. without any string manipulation.
   * TODO: refactor: this should be setDifficulty
   */
  public async _setDifficultyFromUrlOrDefault() {
    if (this.puzzleFrameworkConfig.urlDifficultyParameterEnabled) {
      const hashParameter = this.getDifficultyFromUrl();

      // If in url, use it.
      if (hashParameter) {
        // Store the set difficulty
        this.store.dispatch(
          new SetGameDifficulty({
            difficulty: hashParameter,
          })
        );

        // If not in url and not stored yet, use default.
      } else if (this.currentDifficulty === undefined) {
        // Store the set difficulty
        this.store.dispatch(
          new SetGameDifficulty({
            difficulty: this.puzzleFrameworkConfig.difficultyDefault,
          })
        );
      }

      return this.currentDifficulty;
    }
  }

  /**
   * Returns the language as string, ensures this.currentLanguage is up to date
   * Gets it from the URL or if not set from the default config.
   * Checks if it is a valid value from the config.
   *
   * TODO: refactor: this should be setLanguage
   */
  public async loadLanguage() {
    // If parameter is enabled and set, set it from the parameter, otherwise set default language
    if (this.puzzleFrameworkConfig.urlLanguageParameterEnabled) {
      const hashParameter = this.getLanguageParameterFromUrl();

      // If in url, use it.
      if (hashParameter) {
        // Store the set difficulty
        this.store.dispatch(
          new SetLanguage({
            language: hashParameter,
          })
        );

        // If not in url and not stored yet, use default.
      } else if (this.currentLanguage === undefined) {
        // Store the set difficulty
        this.store.dispatch(
          new SetLanguage({
            language: this.puzzleFrameworkConfig.defaultLanguage,
          })
        );
      }
    } else if (
      (!this.puzzleFrameworkConfig.languagePickerValues || // if language picker has no values, use default
        this.puzzleFrameworkConfig.languagePickerValues.length === 0) &&
      this.currentLanguage !== this.puzzleFrameworkConfig.defaultLanguage
    ) {
      this.store.dispatch(
        new SetLanguage({
          language: this.puzzleFrameworkConfig.defaultLanguage,
        })
      );
    }

    return this.currentLanguage;
  }

  /**
   * Updates the currently active PuzzleFrameworkConfig state from supplied hash parameters in the URL
   *
   */
  private async _updatePuzzleFrameworkConfigFromURLHashParameters() {
    const urlConfigModel = this.getPuzzleConfigFromHashParameters();
    if (Object.keys(urlConfigModel).length) {
      await firstValueFrom(this.store.dispatch(new UpdateFrameworkConfig({ puzzleFrameworkConfig: urlConfigModel })));
    }
  }

  /**
   * Updates the currently active PuzzleFrameworkConfig state with the contents of the configOverride key
   * of a PuzzleFileInfoModel.
   *
   * @param puzzleFileInfo: PuzzleFileInfoModel
   */
  private async _updatePuzzleFrameworkConfigWithOverrideConfig(puzzleFileInfo: string | PuzzleFileInfoModel) {
    // If puzzleFrameworkConfig.puzzleFiles.<puzzleFileInfoName>.configOverride exists, it
    // will contain keys to update the puzzleFrameworkConfig state, which is done here.
    if (typeof puzzleFileInfo === 'object' && puzzleFileInfo.hasOwnProperty('configOverride')) {
      await firstValueFrom(
        this.store.dispatch(
          new UpdateFrameworkConfig({
            puzzleFrameworkConfig: puzzleFileInfo.configOverride,
          })
        )
      );
    }
  }

  private _maybeEnableURLDateParameter() {
    this.urlDateParameterEnabled =
      this.cheatCodeEnabled() || this.puzzleFrameworkConfig.urlDateParameterEnabled
        ? this.puzzleFrameworkConfig.urlDateParameterEnabled
        : false;
  }

  private _maybeEnableDifficultyParameter() {
    this.urlDifficultyParameterEnabled = this.puzzleFrameworkConfig.urlDifficultyParameterEnabled
      ? this.puzzleFrameworkConfig.urlDifficultyParameterEnabled
      : false;
  }

  public replaceDateTag(puzzleFilePath: string, pathDate: Date) {
    // Don't change now as it is used to compare with dateParameter, but copy it from the now date
    // "add" weekStartOffsetFromMonday.
    // The value moves to the left on the timeline so we need to add the inverse or subtract.
    const offsetDate = new Date(pathDate.getTime());

    // setDate only sets the day of the month
    offsetDate.setDate(pathDate.getDate() - this.puzzleFrameworkConfig.weekStartOffsetFromMonday);

    // Find out which day the week starts (0: sunday, 1: monday, ...)
    // console.log(getLocaleFirstDayOfWeek('en-EN'));

    const datePipe = new DatePipe('en-EN');
    const regExp = /\$date\{([\s\S]+)\}/g;
    const matched = regExp.exec(puzzleFilePath);

    const dateString = datePipe.transform(offsetDate, matched[1]);
    puzzleFilePath = puzzleFilePath.replace(matched[0], dateString);
    return puzzleFilePath;
  }

  private async getPuzzleFileFromHTTP(puzzleFilePath) {
    // Support generated puzzles via special scheme: generated:<type>
    if (typeof puzzleFilePath === 'string' && puzzleFilePath.startsWith('generated:')) {
      const type = puzzleFilePath.substring('generated:'.length);
      // Support parameterized hashi generation: generated:hashi[:rows[:columns[:numberOfIslands[:doubleBridges]]]]
      if (type.startsWith('hashi')) {
        const parts = type.split(':'); // ['hashi', rows, columns, islands, double]
        let rows = 10;
        let columns = 10;
        let numberOfIslands = 5;
        let doubleBridges: number | undefined = 0.2;
        if (parts.length >= 2) {
          const v = parseInt(parts[1], 10);
          if (!Number.isNaN(v) && v > 0) rows = v;
        }
        if (parts.length >= 3) {
          const v = parseInt(parts[2], 10);
          if (!Number.isNaN(v) && v > 0) columns = v;
        }
        if (parts.length >= 4) {
          const v = parseInt(parts[3], 10);
          if (!Number.isNaN(v) && v > 0) numberOfIslands = v;
        }
        if (parts.length >= 5) {
          const v = parseFloat(parts[4]);
          if (!Number.isNaN(v) && v >= 0 && v <= 1) doubleBridges = v;
        }
        return generateHashiPuzzle(rows, columns, numberOfIslands, doubleBridges);
      }
      throw new Error(`Unknown generated puzzle type: ${type}`);
    }

    const fileExtension = puzzleFilePath.split('.').pop();

    if (fileExtension === 'xml') {
      const data = await firstValueFrom(
        this.http.get(puzzleFilePath, {
          responseType: 'text',
          params: {
            version: __BUILD_VERSION__,
          },
        })
      );
      const x2js = new X2JS();
      const parsed = x2js.xml2js(data) as any;

      if (parsed.Root.parsererror) {
        console.log(data);
        throw new Error(`No valid xml trying to load '${puzzleFilePath}'`);
      }

      return parsed.Root;
    } else if (fileExtension === 'json') {
      const data = await firstValueFrom(
        this.http.get(puzzleFilePath, {
          responseType: 'text',
          params: {
            version: __BUILD_VERSION__,
          },
        })
      );
      return await JSON.parse(data);
    }
  }

  public getPuzzleFileInfo(puzzleFileInfoName: string) {
    return this.puzzleFrameworkConfig.puzzleFiles[puzzleFileInfoName];
  }

  public async initPuzzleFrameworkConfig(puzzleFileInfoName: string = '') {
    if (puzzleFileInfoName) {
      const puzzleFileInfo = this.getPuzzleFileInfo(puzzleFileInfoName);
      await this._updatePuzzleFrameworkConfigWithOverrideConfig(puzzleFileInfo);
    }

    await this._updatePuzzleFrameworkConfigFromURLHashParameters();

    // TODO: find out if this is the right place for those two
    this._maybeEnableURLDateParameter();
    this._maybeEnableDifficultyParameter();

    // Calculate the difficulty.
    await this._setDifficultyFromUrlOrDefault();

    await this.loadLanguage();
  }

  public getPuzzleFilePath(puzzleFileInfoName: string, fallback: boolean, pathDate?: Date) {
    const puzzleFileInfo = this.getPuzzleFileInfo(puzzleFileInfoName);

    // puzzleFileInfo is either a string with the path, or a more elaborate definition with
    // a property puzzleFile (sic!) that contains the puzzleFilePath
    let puzzleFilePath = typeof puzzleFileInfo === 'object' ? puzzleFileInfo.puzzleFile : puzzleFileInfo;

    // TODO; understand this semantic... when the info is an object, and the caller requested to use the fallback,
    // load the fallbackfile from the object, but if it is not, fuck the fallback, let's dance?
    puzzleFilePath = (
      fallback && typeof puzzleFileInfo === 'object' ? puzzleFileInfo.fallbackFile : puzzleFilePath
    ) as string;

    // at this point, if there's no usable path, eject
    if (!puzzleFilePath || typeof puzzleFilePath !== 'string') {
      console.log('loadPuzzleFile/puzzleFilePath', puzzleFilePath);
      throw new Error(`No puzzleFilePath found or not a string`);
    }

    if (puzzleFilePath.indexOf('$date{') !== -1) {
      // Replace $date{...} with the correct date in file string.
      if (!pathDate) {
        pathDate = this.getCurrentDate();
      }
      puzzleFilePath = this.replaceDateTag(puzzleFilePath, pathDate);
    }

    // Replace $difficulty with the correct difficulty in file string.
    if (puzzleFilePath.indexOf('$difficulty') !== -1) {
      puzzleFilePath = puzzleFilePath.replace('$difficulty', this.currentDifficulty);
    }

    // Check if language parameter should be replaced
    if (puzzleFilePath.indexOf('$language') !== -1) {
      puzzleFilePath = this.replaceLanguageTag(puzzleFilePath);
    }

    return puzzleFilePath;
  }

  /**
   * Loads the puzzle file with "filename" from the assets folder
   * Returns a promise
   *
   * @param puzzleFileInfoName the name of the PuzzleFileInfoModel data to load
   * @param fallback if the fallback file should be loaded
   */
  public async loadPuzzleFile(puzzleFileInfoName: string, fallback: boolean = false) {
    const puzzleFilePath = this.getPuzzleFilePath(puzzleFileInfoName, fallback);

    const puzzleFile = await this.getPuzzleFileFromHTTP(puzzleFilePath);

    // for use for callers that want to do additional stuff, such as call initPuzzle
    // TODO: this whole method has the problem that it changes a lot of state, despite actually
    // only fetching and returning a file. Loading doesn't actually accurately describe it.
    return puzzleFile;
  }

  /**
   * Initializes the puzzle with the given puzzleFile as js-object
   *
   * @param puzzleFile js-object with the puzzle file data
   */
  private initPuzzle(puzzleFile: any) {
    this.puzzleConfig = puzzleFile;
    // this.puzzleID = this.puzzleConfig.puzzleID;
    this.initialized = true;
    if (puzzleFile.hasOwnProperty('puzzle') && puzzleFile.puzzle.hasOwnProperty('_puzzleType')) {
      switch (puzzleFile.puzzle._puzzleType) {
        case PuzzleTypeIdEnum.CROSSWORD_1:
        case PuzzleTypeIdEnum.CROSSWORD_20:
        case PuzzleTypeIdEnum.CROSSWORD_221:
        case PuzzleTypeIdEnum.CROSSWORD_252:
        case PuzzleTypeIdEnum.CROSSWORD_265:
        case PuzzleTypeIdEnum.CROSSWORD_310:
        case PuzzleTypeIdEnum.CROSSWORD_770:
        case PuzzleTypeIdEnum.CROSSWORD_771:
        case PuzzleTypeIdEnum.WORD_SEARCH_600:
        case PuzzleTypeIdEnum.SUDOKU_900:
        case PuzzleTypeIdEnum.HASHI:
          this.puzzleConfigLoaded.next(puzzleFile.puzzle._puzzleType);
          break;
        default:
          const puzzleConfig = this.puzzleFrameworkConfig.puzzleFiles[this.configPuzzleName];
          this.puzzleFileLoadErrors.push(
            'Unknown puzzle type in file: ' +
              (typeof puzzleConfig === 'string' ? puzzleConfig : puzzleConfig.puzzleFile)
          );
          this.puzzleConfigLoaded.next(PuzzleTypeIdEnum.ERROR);
          break;
      }
    } else if (puzzleFile.hasOwnProperty('type') && puzzleFile.type === PuzzleTypeIdEnum.IMAGE_PUZZLE) {
      this.puzzleConfigLoaded.next(PuzzleTypeIdEnum.IMAGE_PUZZLE);
    } else if (
      puzzleFile.hasOwnProperty('puzzle') &&
      Array.isArray(puzzleFile.puzzle) &&
      Array.isArray(puzzleFile.puzzle[0])
    ) {
      // New Hashi JSON format: { puzzle: number[][], solution: string[][] }
      this.puzzleConfigLoaded.next(PuzzleTypeIdEnum.HASHI);
    } else if (puzzleFile.hasOwnProperty('puzzleType')) {
      switch (puzzleFile.puzzleType) {
        case '1000':
          this.puzzleConfigLoaded.next(PuzzleTypeIdEnum.QUIZ_1000);
          break;
      }
    } else {
      console.log('initPuzzle/puzzleFile', puzzleFile);
      console.error(`Unable to initialize provided puzzleFile`);
    }

    this.store.dispatch(
      new LoadUpdateNotifications({
        puzzleTypeId: this.puzzleConfigLoaded.getValue(),
        language: this.currentLanguage,
      })
    );
  }

  buttonBarEmitButton(eventName: string) {
    this.buttonPressed.next(eventName);
  }

  showBrowserWarningBadge() {
    this.browserWarning.next(true);
  }

  preparePrivacyUrl(inputString: string, analyticsEnabled: boolean = false) {
    inputString = this.replaceLanguageTag(inputString);
    inputString = this.replaceCustomerOfTag(inputString);
    inputString = this.replaceAnalyticsTag(inputString, analyticsEnabled);
    inputString = this.addCacheBuster(inputString);
    return this.sanitizer.bypassSecurityTrustResourceUrl(inputString);
  }

  addCacheBuster(inputString: string) {
    // Add a cache buster to the URL
    const cacheBuster = `?cb=${new Date().getTime()}`;
    return inputString + cacheBuster;
  }

  replaceLanguageTag(inputString: string) {
    if (!this.puzzleFrameworkConfig.languagePickerValues || !this.puzzleFrameworkConfig.languagePickerValues.length) {
      if (inputString.indexOf('$language{name}') !== -1) {
        inputString = inputString.replace('$language{name}', this.currentLanguage);
      }
      return inputString;
    }

    const language = this.puzzleFrameworkConfig.languagePickerValues.filter(
      (lang) => lang.name === this.currentLanguage
    );
    if (language.length !== 0) {
      const lang = language[0];
      if (inputString.indexOf('$language{slug}') !== -1) {
        inputString = inputString.replace('$language{slug}', lang.slug);
      }
      if (inputString.indexOf('$language{name}') !== -1) {
        inputString = inputString.replace('$language{name}', lang.name);
      }
    }
    return inputString;
  }

  replaceCustomerOfTag(inputString: string) {
    if (inputString.indexOf('$customerOf{name}') !== -1) {
      inputString = inputString.replace('$customerOf{name}', this.puzzleFrameworkConfig.customerOf);
    }
    return inputString;
  }

  replaceAnalyticsTag(inputString: string, enabled: boolean = false) {
    if (inputString.indexOf('$analytics') !== -1) {
      const analytics_string = enabled ? 'with-analytics' : 'without-analytics';
      inputString = inputString.replace('$analytics', analytics_string);
    }
    return inputString;
  }

  cheatCodeEnabled() {
    const hashParameters = this.getHashParameters();
    return (
      this.softCheatCodeEnabled ||
      (hashParameters.hasOwnProperty('rumpelstilzchen') && hashParameters.rumpelstilzchen === __BUILD_VERSION__)
    );
  }

  getTranslationKey() {
    if (this.puzzleFrameworkConfig.translationKey) {
      return this.puzzleFrameworkConfig.translationKey;
    }
    return Object.keys(PuzzleTypeIdEnum)[Object.values(PuzzleTypeIdEnum).indexOf(this.puzzleConfigLoaded.getValue())];
  }

  async markUpdateNotificationsAsRead() {
    this.store.dispatch(new MarkAsRead({ puzzleTypeId: this.puzzleConfigLoaded.getValue() }));
    this.store.dispatch(
      new LoadUpdateNotifications({
        puzzleTypeId: this.puzzleConfigLoaded.getValue(),
        language: this.currentLanguage,
      })
    );
  }

  toggleRumpelstilzchen(reload = true) {
    if (reload) {
      const hashParameters = this.getHashParameters();
      if (hashParameters.hasOwnProperty('rumpelstilzchen') && hashParameters.rumpelstilzchen === __BUILD_VERSION__) {
        delete hashParameters.rumpelstilzchen;
      } else {
        hashParameters.rumpelstilzchen = __BUILD_VERSION__;
      }
      window.location.hash =
        '#' +
        Object.keys(hashParameters)
          .map((key) => key + '=' + hashParameters[key])
          .join('&');

      window.location.reload();
    } else {
      this.softCheatCodeEnabled = !this.softCheatCodeEnabled;
    }
  }
}
